import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import { MONO_FONT, VISUALLY_HIDDEN } from '@/theme';
import Typography from '@mui/material/Typography';

// El glifo es decorativo (aria-hidden) y el texto solo-lectores dice lo mismo en palabras.
const Check = () => (
  <Box
    component="span"
    sx={{ color: 'text.accent', fontFamily: MONO_FONT, fontSize: '14px' }}
  >
    <span aria-hidden="true">✓</span>
    <Box component="span" sx={VISUALLY_HIDDEN}>
      <T>Incluido</T>
    </Box>
  </Box>
);

const Dash = () => (
  <Box component="span" sx={{ color: 'grey.300', fontFamily: MONO_FONT }}>
    <span aria-hidden="true">—</span>
    <Box component="span" sx={VISUALLY_HIDDEN}>
      <T>No incluido</T>
    </Box>
  </Box>
);

// `caption` (opcional) nombra la tabla para lectores de pantalla sin mostrarse.
// `featureWidth` (opcional) es el ancho de la columna de capacidades; 30% por defecto.
// `divided` (opcional) separa las columnas de valores con un filete, como la tabla de /planes;
// la destacada queda cerrada a ambos lados en primary.light.
// Cada columna puede llevar `sub`: el nombre va en una línea y `sub` debajo, en mono.

// Bordes laterales de una celda de valor cuando la tabla va `divided`.
const dividers = (divided, col, last) =>
  divided
    ? {
        borderLeft: '1px solid',
        borderLeftColor: col.featured ? 'primary.light' : 'grey.200',
        ...(col.featured && last ? { borderRight: '1px solid', borderRightColor: 'primary.light' } : {}),
      }
    : {};

export default function CompareTable({ columns = [], rows = [], caption, featureWidth = '30%', divided = false }) {
  return (
    <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: '600px',
          borderCollapse: 'collapse',
          fontSize: '14px',
        }}
      >
        {caption && (
          <Box component="caption" sx={VISUALLY_HIDDEN}>
            <T>{caption}</T>
          </Box>
        )}
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              scope="col"
              sx={{
                py: '12px',
                pr: '24px',
                textAlign: 'left',
                fontFamily: MONO_FONT,
                fontSize: '11px',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.muted',
                fontWeight: 500,
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                width: featureWidth,
              }}
            >
              <Box component="span" sx={VISUALLY_HIDDEN}>
                <T>Capacidad</T>
              </Box>
            </Box>
            {columns.map((col, i) => (
              <Box
                component="th"
                scope="col"
                key={i}
                sx={{
                  py: '12px',
                  px: '16px',
                  textAlign: 'center',
                  fontFamily: MONO_FONT,
                  fontSize: '11px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: col.featured ? 'text.accent' : 'grey.600',
                  fontWeight: 500,
                  borderBottom: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: col.featured ? 'primary.lightest' : 'transparent',
                  ...(divided && col.featured ? { borderTop: '1px solid', borderTopColor: 'primary.light' } : {}),
                  ...dividers(divided, col, i === columns.length - 1),
                }}
              >
                {col.sub ? (
                  <>
                    <Typography
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        color: col.featured ? 'primary.darker' : 'text.primary',
                      }}
                    >
                      <T>{col.label}</T>
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        fontSize: '10px',
                        letterSpacing: '0.08em',
                        textTransform: 'none',
                        color: col.featured ? 'text.accent' : 'text.muted',
                      }}
                    >
                      <T>{col.sub}</T>
                    </Box>
                  </>
                ) : (
                  <T>{col.label}</T>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {rows.map((row, ri) => (
            <Box component="tr" key={ri}>
              <Box
                component="th"
                scope="row"
                sx={{
                  py: '14px',
                  pr: '24px',
                  borderBottom: '1px solid',
                  borderColor: 'grey.100',
                  verticalAlign: 'top',
                  textAlign: 'left',
                  fontWeight: 400,
                }}
              >
                {row.group && (
                  <Typography
                    sx={{
                      fontFamily: MONO_FONT,
                      fontSize: '10px',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'text.muted',
                      mb: 0.5,
                    }}
                  >
                    <T>{row.group}</T>
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: 'text.primary',
                    fontWeight: 500,
                  }}
                >
                  <T>{row.feature}</T>
                </Typography>
                {row.note && (
                  <Typography
                    sx={{ fontSize: '12px', color: 'text.muted', mt: 0.5 }}
                  >
                    <T>{row.note}</T>
                  </Typography>
                )}
              </Box>

              {columns.map((col, ci) => {
                const val = row.values?.[ci];
                return (
                  <Box
                    component="td"
                    key={ci}
                    sx={{
                      py: '14px',
                      px: '16px',
                      textAlign: 'center',
                      borderBottom: '1px solid',
                      borderColor: 'grey.100',
                      bgcolor: col.featured
                        ? 'primary.lightest'
                        : 'transparent',
                      fontSize: '14px',
                      color: 'grey.700',
                      verticalAlign: 'middle',
                      ...dividers(divided, col, ci === columns.length - 1),
                      ...(divided && col.featured && ri === rows.length - 1
                        ? { borderBottomColor: 'primary.light' }
                        : {}),
                    }}
                  >
                    <T>
                      {val === true ? (
                        <Check />
                      ) : val === false || val == null ? (
                        <Dash />
                      ) : (
                        val
                      )}
                    </T>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
